import { useAppContext } from "AppContext";
import {
  AssetImage,
  EnumOwnerType,
  useOrganizationsByIdsQuery,
} from "gql/generated";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ConnectionCard,
  DoorstopCard,
  DoorstopItem,
  DoorstopLayout,
  EmptyState,
  LoadingPage,
  ViewSubSection,
} from "shared/components";
import {
  formatPhoneNumber,
  getAddressString,
  getMultilingualString,
} from "shared/utils";
import { Grid } from "styled-system/jsx";
import {
  AddBusinessListingModal,
  OperatorDoorstopEmptyCard,
} from "./components";
import { useState } from "react";

export const OperatorHome = () => {
  const { t } = useTranslation("core", { keyPrefix: "DOORSTOP" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const {
    organizations,
    operators,
    operatorsError,
    operatorsLoading,
    updateUserAccessStatus,
  } = useAppContext();

  // Only DMO users will have organizations and they shouldn't see this screen
  if (organizations.length > 0) {
    // This avoids a fun error when you're editing an operator as an operator user
    // when you also have DMO access and you click the Whereabouts logo to go home
    // React attempts two subsequent navigations and it throws an error
    setTimeout(() => navigate("/app"), 0);
  }

  const connectedOrgIds = Array.from(
    new Set(
      operators.flatMap((op) => op.enrollments.map((e) => e?.organization)),
    ),
  );

  const {
    data,
    loading: connectionsLoading,
    error: connectionsError,
  } = useOrganizationsByIdsQuery({
    variables: { ids: connectedOrgIds },
  });

  if (connectionsLoading || operatorsLoading) {
    return <LoadingPage />;
  }

  if (connectionsError || operatorsError) {
    throw new Error("Could not load user data");
  }

  const operatorGroup = {
    title: t("OPERATORS.TITLE"),
    description: t("OPERATORS.DESCRIPTION"),
    items: _.sortBy(
      operators?.map((op) => ({
        _entityId: op._entityId,
        path: `/app/operator/${op._entityId}`,
        name: getMultilingualString(op.name),
        image: op.images?.[0] as AssetImage,
        type: EnumOwnerType.Operator,
        claimed: op.claimed,
        address: op.contact.address
          ? getAddressString(op.contact.address)
          : undefined,
      })) || [],
      "name",
    ),
  };
  const connectionsGroup = {
    title: t("CONNECTIONS.TITLE"),
    description: t("CONNECTIONS.DESCRIPTION"),
    items: _.sortBy(
      data?.organizationsByIds?.map((org) => ({
        _entityId: org._id,
        name: getMultilingualString(org.name),
        image: org.logo as AssetImage | null | undefined,
        type: EnumOwnerType.Organization,
        website: org.contact?.links?.website,
        email: org.contact?.email,
        phone: org.contact?.phone,
      })) || [],
      "name",
    ),
  };

  /**
   * When a user logs into a given operator,
   * we need to set their access status to Accepted
   * @param item
   */
  const handleNavigate = async (item: DoorstopItem) => {
    await updateUserAccessStatus(item.type, item._entityId);
    navigate(item.path);
  };

  const handleAddNewListingClick = () => setIsModalOpen(true);

  return (
    <>
      <DoorstopLayout operatorsOnly>
        <ViewSubSection
          title={operatorGroup.title}
          description={operatorGroup.description}
          buttonLabel={`+ ${t("OPERATORS.ADD_NEW_LISTING")}`}
          onButtonClick={handleAddNewListingClick}
          width="full"
          maxW="800px"
          gap={"300"}
          mb="600"
        >
          <Grid
            gridTemplateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={"300"}
          >
            {operatorGroup.items.map((item) => (
              <DoorstopCard
                key={item._entityId}
                doorstopItem={item}
                onItemClick={handleNavigate}
              />
            ))}

            <OperatorDoorstopEmptyCard
              numOfOperators={operatorGroup.items.length}
              onAddListingClick={handleAddNewListingClick}
            />
          </Grid>
        </ViewSubSection>

        <ViewSubSection
          title={connectionsGroup.title}
          description={connectionsGroup.description}
          width="full"
          maxW="800px"
          gap={"300"}
        >
          {connectionsGroup.items.map((item) => (
            <ConnectionCard
              key={item._entityId}
              image={item.image}
              name={item.name}
              email={item.email}
              phone={item.phone ? formatPhoneNumber(item.phone) : undefined}
              website={item.website}
            />
          ))}
          {connectionsGroup.items.length < 1 && (
            <EmptyState text={t("CONNECTIONS.EMPTY")} />
          )}
        </ViewSubSection>
      </DoorstopLayout>
      <AddBusinessListingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        connectedOrganizationIds={connectedOrgIds}
      />
    </>
  );
};
